import { FC, useState, useEffect, useCallback, useContext } from 'react';
import { TaxReturnDeliveryDialogProps } from './TaxReturnDeliveryDialog.types';
import styles from './TaxReturnDeliveryDialog.module.css';
import {
  Loading,
  Flex,
  Button,
  Text,
  ButtonGroup,
  Dialog,
} from '@/_components';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';

export const TaxReturnDeliveryDialog: FC<TaxReturnDeliveryDialogProps> = ({
  taxReturn,
  onClose,
}) => {
  const [loaded, setLoaded] = useState(false);

  const [bypassDraft, setBypassDraft] = useState(false);

  const isDraft =
    taxReturn?.draft_status === 'draft' ||
    taxReturn?.draft_status === 'change_requested';

  const changeRequested = taxReturn?.draft_status === 'change_requested';

  const draftComment = taxReturn?.draft_comment;
  const taxReturnId = taxReturn?.id;
  const companyId = taxReturn?.company_id;

  const { setSending, setSelectedTaxReturnForReplacement } = useContext(
    AdminTaxReturnsContext,
  );

  const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);

  const onElementAvailable = useCallback(
    (selector: any, callback: any) => {
      if (!iframeRef) {
        return;
      }

      const iframeDoc =
        iframeRef.contentDocument || iframeRef.contentWindow?.document;

      if (!iframeDoc) {
        return;
      }

      const observer = new MutationObserver((mutationsList, observer) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const element = iframeDoc.querySelector(selector);
            if (element) {
              callback(element);
              observer.disconnect();
              return;
            }
          }
        }
      });

      observer.observe(iframeDoc, { childList: true, subtree: true });

      // Check if the element is already present
      const initialCheck = iframeDoc.querySelector(selector);

      if (initialCheck) {
        callback(initialCheck);
        observer.disconnect();
      }
    },
    [iframeRef],
  );

  const handleIframeLoad = useCallback(() => {
    if (iframeRef?.contentWindow) {
      (iframeRef as any).contentWindow.isEmbeddedInNext = true;
    }

    onElementAvailable('#app', () => {
      setLoaded(true);
    });
  }, [iframeRef, onElementAvailable]);

  useEffect(() => {
    if (!iframeRef) return;

    iframeRef.addEventListener('load', handleIframeLoad);

    return () => {
      iframeRef.removeEventListener('load', handleIframeLoad);
    };
  }, [iframeRef, handleIframeLoad]);

  useEffect(() => {
    const handler = () => {
      if (onClose) {
        onClose();
      }
    };

    globalThis.document.addEventListener('closeDialog', handler);

    return () => {
      globalThis.document.removeEventListener('closeDialog', handler);
    };
  }, [onClose]);

  const onDialogClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const replaceTaxReturnDraft = () => {
    setSending(true);
    setSelectedTaxReturnForReplacement(taxReturn);
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {isDraft && !bypassDraft ? (
        <Dialog isOpen={true} onClose={onDialogClose}>
          <Dialog.Content>
            <Text fontWeight="bold">
              Tax Return Draft{' '}
              {changeRequested
                ? '- Changes requested'
                : '- Under client review'}
            </Text>
            <Text>
              {changeRequested
                ? 'The client has requested changes to the tax return with the following reason: '
                : 'This is a draft of the tax return. Do you want to start classification?'}
            </Text>
            {draftComment && <Text>{draftComment}</Text>}
          </Dialog.Content>
          <Dialog.Footer>
            <ButtonGroup align="right">
              {changeRequested && (
                <Button
                  intent="secondary"
                  value="Replace Tax Return Draft"
                  onClick={replaceTaxReturnDraft}
                />
              )}
              <Button intent="secondary" value="Close" onClick={onClose} />
              <Button
                intent="primary"
                value="Start Classification"
                onClick={() => {
                  setBypassDraft(true);
                }}
              />
            </ButtonGroup>
          </Dialog.Footer>
        </Dialog>
      ) : (
        <Flex className={styles.dialogContainer} isYCentered isXCentered>
          <>
            {!loaded && <Loading size={40} />}
            <iframe
              ref={setIframeRef}
              src={`/tax-return-delivery-admin-view/${taxReturnId}?companyId=${companyId}`}
              className={styles.iframe}
              style={{ display: loaded ? 'block' : 'none' }}
            />
          </>
        </Flex>
      )}
    </>
  );
};
